import axios from 'src/lib/axios';

import { JWT_STORAGE_KEY } from './constant';

export interface JwtPayload {
  user_id: number;
  role_id?: number;
  exp: number;
  iat: number;
  sub?: string;
  auth?: string;
}

let tokenExpirationTimeout: ReturnType<typeof setTimeout> | null = null;

export function jwtDecode(token: string): JwtPayload | null {
  try {
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length < 2) {
      throw new Error('Invalid token!');
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    return JSON.parse(atob(base64)) as JwtPayload;
  } catch (error) {
    console.error('Error decoding token:', error);
    throw error;
  }
}

export function extractAssumedRoleId(token: string): number | null {
  try {
    if (!token) {
      return null;
    }

    const decoded = jwtDecode(token);
    return decoded?.role_id ?? null;
  } catch (error) {
    console.error('Error extracting assumed role ID:', error);
    return null;
  }
}

export function isValidToken(accessToken: string) {
  if (!accessToken) {
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);

    if (!decoded || !('exp' in decoded)) {
      return false;
    }

    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  } catch (error) {
    console.error('Error during token validation:', error);
    return false;
  }
}

export function tokenExpired(exp: number) {
  if (tokenExpirationTimeout) {
    clearTimeout(tokenExpirationTimeout);
    tokenExpirationTimeout = null;
  }
}

export async function setSession(accessToken: string | null): Promise<void> {
  try {
    if (accessToken) {
      localStorage.setItem(JWT_STORAGE_KEY, accessToken);

      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      const decodedToken = jwtDecode(accessToken);

      if (decodedToken && 'exp' in decodedToken) {
        tokenExpired(decodedToken.exp);
      } else {
        throw new Error('Invalid access token!');
      }
    } else {
      localStorage.removeItem(JWT_STORAGE_KEY);
      delete axios.defaults.headers.common.Authorization;
    }
  } catch (error) {
    console.error('Error during set session:', error);
    throw error;
  }
}
